import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { startRegistration } from "@simplewebauthn/browser";
import { api, fmtMoney, fmtWhen } from "../../lib/peApi";
import { usePEAuth } from "./PEAuthContext";
import { PEFormRenderer } from "./PEForms";

const inputCls =
  "w-full rounded-sm border border-pe-sand/70 bg-white px-4 py-3 text-sm text-pe-forest outline-none focus:border-pe-gold transition-colors placeholder:text-pe-muted/60";

const chip = (on) =>
  `px-3.5 py-2 rounded-full border text-[12px] transition-colors ${on ? "border-pe-forest bg-pe-forest text-pe-ivory" : "border-pe-sand/70 bg-white text-pe-body hover:border-pe-gold"}`;

const TABS = [
  ["overview", "Overview"],
  ["appointments", "Appointments"],
  ["profile", "Profile & Preferences"],
  ["forms", "Consultation & Intake"],
  ["notes", "Notes & Improvements"],
  ["reviews", "Reviews"],
];

const STATUS_CLS = {
  confirmed: "bg-pe-cream text-pe-forest border-pe-gold/50",
  pending_payment: "bg-amber-50 text-amber-800 border-amber-200",
  pending_review: "bg-amber-50 text-amber-800 border-amber-200",
  completed: "bg-pe-cream text-pe-olive border-pe-sage/50",
  canceled: "bg-red-50 text-red-800 border-red-200",
  rescheduled: "bg-pe-cream text-pe-olive border-pe-sage/50",
  expired: "bg-red-50 text-red-800 border-red-200",
  no_show: "bg-red-50 text-red-800 border-red-200",
};

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-sans text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-1.5">{label}</span>
      {children}
    </label>
  );
}

export default function PEAccountPage() {
  const { user, setUser, logout } = usePEAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user === null) navigate("/prismatic-essence/auth");
  }, [user, navigate]);

  const load = () => api("/me/dashboard").then(setData).catch((e) => setErr(e.message));
  useEffect(() => {
    if (user) load();
  }, [user]);

  if (!user || !data) {
    return <div className="min-h-screen bg-pe-ivory flex items-center justify-center"><p className="font-pe italic text-xl text-pe-olive">{err || "Opening your dashboard…"}</p></div>;
  }

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 4000); };
  const upcoming = data.appointments.filter((a) => ["confirmed", "pending_payment", "pending_review"].includes(a.status) && new Date(a.start) > new Date());
  const past = data.appointments.filter((a) => !upcoming.includes(a));
  const reviewable = data.appointments.filter((a) => a.status === "completed" && !data.reviews.find((r) => r.appointment_id === a.booking_id));

  return (
    <main id="main-content" data-testid="pe-account" className="min-h-screen bg-pe-ivory py-24 px-5">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <Link to="/prismatic-essence" className="text-[10px] tracking-[0.25em] uppercase text-pe-muted hover:text-pe-forest">← Prismatic Essence</Link>
            <h1 className="font-pe text-3xl md:text-4xl text-pe-forest mt-3">Welcome, {user.preferred_name || user.name}.</h1>
          </div>
          <button data-testid="pe-logout" onClick={async () => { await logout(); navigate("/prismatic-essence"); }} className="text-[10px] tracking-[0.25em] uppercase text-pe-olive hover:text-pe-forest underline underline-offset-4">Sign out</button>
        </div>

        {msg && <p role="status" aria-live="polite" className="mb-6 text-sm text-pe-forest bg-pe-cream border border-pe-gold/40 rounded-sm px-4 py-3">{msg}</p>}

        <div className="flex flex-wrap gap-2 mb-10" role="tablist">
          {TABS.map(([id, label]) => (
            <button
              key={id}
              data-testid={`pe-tab-${id}`}
              role="tab"
              aria-selected={tab === id}
              onClick={() => setParams({ tab: id }, { replace: true })}
              className={`rounded-full px-4 py-2 font-sans text-[10px] tracking-[0.2em] uppercase border transition-colors ${tab === id ? "bg-pe-forest text-pe-ivory border-pe-forest" : "border-pe-sand/70 text-pe-olive hover:border-pe-gold"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="grid md:grid-cols-3 gap-6">
            <div data-testid="pe-loyalty-card" className="rounded-sm border border-pe-sand/60 bg-white p-7">
              <p className="text-[10px] tracking-[0.25em] uppercase text-pe-olive mb-2">Loyalty points</p>
              <p className="font-pe text-5xl text-pe-forest">{data.loyalty.balance}</p>
              <p className="text-[12px] text-pe-muted mt-3">{data.loyalty.redemption_status === "not_configured" ? "Redemption value is being set by the house — you'll hear first." : ""}</p>
            </div>
            <div data-testid="pe-fifth-card" className="rounded-sm border border-pe-sand/60 bg-white p-7">
              <p className="text-[10px] tracking-[0.25em] uppercase text-pe-olive mb-2">Fifth-service reward</p>
              <p className="font-pe text-3xl text-pe-forest">{data.fifth.progress} of {data.fifth.milestone}</p>
              <p className="text-[12px] text-pe-muted mt-3">eligible services completed — your {data.fifth.milestone}th earns 50% off.</p>
            </div>
            <div data-testid="pe-referral-card" className="rounded-sm border border-pe-sand/60 bg-white p-7">
              <p className="text-[10px] tracking-[0.25em] uppercase text-pe-olive mb-2">Your referral code</p>
              <p className="font-pe text-3xl text-pe-forest">{data.referrals.code}</p>
              <button
                data-testid="pe-referral-copy"
                onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/prismatic-essence/auth?ref=${data.referrals.code}`); flash("Referral link copied."); }}
                className="mt-3 rounded-full border border-pe-brass/60 px-4 py-1.5 text-[10px] tracking-[0.2em] uppercase text-pe-forest hover:bg-pe-gold/10"
              >
                Copy referral link
              </button>
              {data.referrals.config_recipient === "unset" && <p className="text-[11px] text-pe-muted mt-3">Referral rewards are being configured by the house — your referrals still count.</p>}
            </div>
            {data.first_time_eligible && (
              <div className="md:col-span-3 rounded-sm border border-pe-gold/50 bg-pe-cream/60 p-5 text-sm text-pe-forest">
                Your first service earns <strong>20% off</strong> — applied automatically when you book. The $25 deposit still secures your time.
              </div>
            )}
            <GuestClaim flash={flash} reload={load} />
          </div>
        )}

        {tab === "appointments" && (
          <div className="space-y-4">
            {data.policies?.cancellation && (
              <div className="rounded-sm border border-pe-sand/60 bg-pe-cream/50 p-5 space-y-2" data-testid="pe-account-policies">
                <p className="font-pe text-lg text-pe-forest">Cancellation & rescheduling policy</p>
                <p className="text-[12px] text-pe-body leading-relaxed">{data.policies.cancellation}</p>
                <p className="text-[12px] text-pe-body leading-relaxed">{data.policies.rescheduling}</p>
              </div>
            )}
            {!data.appointments.length && <p className="text-pe-muted text-sm">No appointments yet — <Link to="/prismatic-essence?book=" className="text-pe-brass underline underline-offset-4">book your first</Link>.</p>}
            {[["Upcoming", upcoming], ["Past", past]].map(([label, list]) => (
              <div key={label}>
                <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-pe-olive mb-3">{label}</p>
                {list.map((a) => (
                  <ClientAppointment key={a.booking_id} a={a} policies={data.policies} reload={async (m) => { if (m) flash(m); await load(); }} />
                ))}
              </div>
            ))}
          </div>
        )}

        {tab === "profile" && <ProfileTab user={user} setUser={setUser} flash={flash} />}

        {tab === "forms" && <FormsTab flash={flash} />}

        {tab === "notes" && <NotesTab notes={data.notes} reload={load} flash={flash} />}

        {tab === "reviews" && <ReviewsTab reviews={data.reviews} reviewable={reviewable} reload={load} flash={flash} />}
      </div>
    </main>
  );
}

function ProfileTab({ user, setUser, flash }) {
  const [form, setForm] = useState({
    name: user.name, preferred_name: user.preferred_name, email: user.email, phone: user.phone,
    address: user.address || "", comms_method: user.comms_method, marketing_opt_in: user.marketing_opt_in,
    reminder_email: user.reminder_prefs?.email !== false, reminder_sms: !!user.reminder_prefs?.sms,
    music_genres: user.prefs?.music_genres || [], music_other: user.prefs?.music_other || "",
    music_volume: user.prefs?.music_volume || "no-preference", music_artists: user.prefs?.music_artists || "",
    entertainment: user.prefs?.entertainment || [], entertainment_other: user.prefs?.entertainment_other || "",
    conversation: user.prefs?.conversation || "no-preference", comfort_note: user.prefs?.comfort_note || "",
  });
  const [prefOptions, setPrefOptions] = useState(null);
  const [pw, setPw] = useState({ current: "", next: "" });
  const [passkeys, setPasskeys] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api("/preferences/options").then(setPrefOptions).catch(() => {});
    api("/auth/passkey/mine").then((d) => setPasskeys(d.passkeys)).catch(() => setPasskeys([]));
  }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async () => {
    setErr("");
    try {
      const updated = await api("/me/profile", {
        method: "PUT",
        body: {
          name: form.name, preferred_name: form.preferred_name, email: form.email, phone: form.phone, address: form.address,
          comms_method: form.comms_method, marketing_opt_in: form.marketing_opt_in,
          reminder_prefs: { email: form.reminder_email, sms: form.reminder_sms },
          prefs: {
            music_genres: form.music_genres, music_other: form.music_other, music_volume: form.music_volume, music_artists: form.music_artists,
            entertainment: form.entertainment, entertainment_other: form.entertainment_other, conversation: form.conversation, comfort_note: form.comfort_note,
          },
        },
      });
      setUser(updated);
      flash("Profile saved.");
    } catch (e) { setErr(e.message); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-8">
      <div className="rounded-sm border border-pe-sand/60 bg-white p-7 space-y-4">
        <p className="font-pe text-xl text-pe-forest">Your details</p>
        {err && <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-sm px-3 py-2">{err}</p>}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Full name"><input className={inputCls} value={form.name} onChange={set("name")} /></Field>
          <Field label="Preferred name"><input className={inputCls} value={form.preferred_name} onChange={set("preferred_name")} /></Field>
        </div>
        <Field label="Email"><input type="email" className={inputCls} value={form.email} onChange={set("email")} /></Field>
        <Field label="Mobile number"><input className={inputCls} value={form.phone} onChange={set("phone")} /></Field>
        <Field label="Preferred communication">
          <select className={inputCls} value={form.comms_method} onChange={set("comms_method")}>
            <option value="email">Email</option><option value="sms">Text message</option><option value="either">Either is fine</option>
          </select>
        </Field>
        <Field label="Appointment reminders">
          <div className="flex gap-5 text-sm text-pe-body pt-1">
            <label className="flex items-center gap-2"><input type="checkbox" className="accent-[#C59B27]" checked={form.reminder_email} onChange={set("reminder_email")} /> Email</label>
            <label className="flex items-center gap-2"><input type="checkbox" className="accent-[#C59B27]" checked={form.reminder_sms} onChange={set("reminder_sms")} /> Text message</label>
          </div>
        </Field>
        <label className="flex items-center gap-2 text-sm text-pe-body"><input type="checkbox" className="accent-[#C59B27]" checked={form.marketing_opt_in} onChange={set("marketing_opt_in")} /> Occasional news and offers (optional)</label>
        <button data-testid="pe-profile-save" onClick={save} className="rounded-full bg-pe-forest text-pe-ivory px-7 py-2.5 text-[10px] tracking-[0.25em] uppercase hover:bg-pe-olive transition-colors">Save changes</button>
        <p className="text-[11px] text-pe-muted pt-2">Account deletion: our retention and deletion policy is being finalized before launch — request deletion anytime via a note in "Notes & Improvements" and the house will handle it personally.</p>
      </div>

      <div className="space-y-8">
        <div className="rounded-sm border border-pe-sand/60 bg-white p-7 space-y-5" data-testid="pe-preferences-card">
          <div>
            <p className="font-pe text-xl text-pe-forest">Music & Entertainment Preferences</p>
            <p className="text-[11px] text-pe-muted mt-1">Optional — they shape a comfortable appointment and are shared with your provider. They're preferences, not promises: specific songs, artists or formats can't be guaranteed.</p>
          </div>
          {prefOptions && (
            <>
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-2">Music you enjoy (choose any)</p>
                <div className="flex flex-wrap gap-2">
                  {prefOptions.music_genres.map((g) => {
                    const on = form.music_genres.includes(g);
                    return <button key={g} type="button" data-testid={`pe-pref-genre-${g.slice(0, 12).replace(/\W+/g, "-").toLowerCase()}`} onClick={() => setForm((f) => ({ ...f, music_genres: on ? f.music_genres.filter((x) => x !== g) : [...f.music_genres, g] }))} className={chip(on)}>{g}</button>;
                  })}
                </div>
                {form.music_genres.includes("Other — specify") && <input className={`${inputCls} mt-3`} placeholder="Tell us what else you enjoy…" value={form.music_other} onChange={(e) => setForm((f) => ({ ...f, music_other: e.target.value }))} />}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Background volume">
                  <select data-testid="pe-pref-volume" className={inputCls} value={form.music_volume} onChange={(e) => setForm((f) => ({ ...f, music_volume: e.target.value }))}>
                    {prefOptions.music_volume.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
                  </select>
                </Field>
                <Field label="Conversation">
                  <select data-testid="pe-pref-conversation" className={inputCls} value={form.conversation} onChange={(e) => setForm((f) => ({ ...f, conversation: e.target.value }))}>
                    {prefOptions.conversation.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Artists or songs you love (optional)">
                <input data-testid="pe-pref-artists" className={inputCls} value={form.music_artists} onChange={(e) => setForm((f) => ({ ...f, music_artists: e.target.value }))} placeholder="A few favorites — requests can't be guaranteed" />
              </Field>
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-2">Entertainment during appointments (choose any)</p>
                <div className="flex flex-wrap gap-2">
                  {prefOptions.entertainment.map((g) => {
                    const on = form.entertainment.includes(g);
                    return <button key={g} type="button" data-testid={`pe-pref-ent-${g.slice(0, 12).replace(/\W+/g, "-").toLowerCase()}`} onClick={() => setForm((f) => ({ ...f, entertainment: on ? f.entertainment.filter((x) => x !== g) : [...f.entertainment, g] }))} className={chip(on)}>{g}</button>;
                  })}
                </div>
                {form.entertainment.includes("Other — specify") && <input className={`${inputCls} mt-3`} placeholder="Something else you enjoy…" value={form.entertainment_other} onChange={(e) => setForm((f) => ({ ...f, entertainment_other: e.target.value }))} />}
              </div>
              <Field label="Comfort note (optional)"><textarea rows={2} className={inputCls} value={form.comfort_note} onChange={(e) => setForm((f) => ({ ...f, comfort_note: e.target.value }))} placeholder="Volume, topics to avoid, anything that helps you relax…" /></Field>
              <p className="text-[11px] text-pe-muted">Save with "Save changes" above. Please don't include sensitive medical details here — your intake form covers safety privately.</p>
            </>
          )}
        </div>

        <div className="rounded-sm border border-pe-sand/60 bg-white p-7 space-y-4" data-testid="pe-passkeys-card">
          <p className="font-pe text-xl text-pe-forest">Passkeys</p>
          <p className="text-[12px] text-pe-muted leading-relaxed">Sign in with this device's face, fingerprint or PIN — no password needed. Passkeys are bound to this website's domain.</p>
          {(passkeys || []).map((k) => (
            <div key={k.credential_id} className="flex items-center justify-between gap-3 text-sm border-b border-pe-sand/40 pb-2">
              <span className="text-pe-body">{k.label} · added {k.created_at ? new Date(k.created_at).toLocaleDateString() : ""}</span>
              <button
                data-testid={`pe-passkey-remove-${k.credential_id.slice(0, 8)}`}
                onClick={async () => { await api(`/auth/passkey/${encodeURIComponent(k.credential_id)}`, { method: "DELETE" }); setPasskeys((p) => p.filter((x) => x.credential_id !== k.credential_id)); flash("Passkey removed."); }}
                className="text-[10px] tracking-[0.2em] uppercase text-red-800"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            data-testid="pe-passkey-add"
            onClick={async () => {
              setErr("");
              try {
                const optRes = await api("/auth/passkey/register/options", { method: "POST", body: {} });
                const credential = await startRegistration({ optionsJSON: JSON.parse(optRes.options) });
                await api("/auth/passkey/register/verify", { method: "POST", body: { state_id: optRes.state_id, credential, label: "This device" } });
                const d = await api("/auth/passkey/mine");
                setPasskeys(d.passkeys);
                flash("Passkey added — you can sign in with it next time.");
              } catch (e) { setErr(e.name === "NotAllowedError" ? "Passkey setup was cancelled." : e.message); }
            }}
            className="rounded-full border border-pe-brass/60 px-7 py-2.5 text-[10px] tracking-[0.25em] uppercase text-pe-forest hover:bg-pe-gold/10 transition-colors"
          >
            Add a passkey
          </button>
        </div>

        <div className="rounded-sm border border-pe-sand/60 bg-white p-7 space-y-4">
          <p className="font-pe text-xl text-pe-forest">Change password</p>
          <Field label="Current password"><input type="password" className={inputCls} value={pw.current} onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))} /></Field>
          <Field label="New password"><input type="password" className={inputCls} value={pw.next} onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))} /></Field>
          <button
            data-testid="pe-password-change"
            onClick={async () => {
              try { await api("/auth/change-password", { method: "POST", body: { current: pw.current, new: pw.next } }); setPw({ current: "", next: "" }); flash("Password updated."); }
              catch (e) { setErr(e.message); }
            }}
            className="rounded-full border border-pe-brass/60 px-7 py-2.5 text-[10px] tracking-[0.25em] uppercase text-pe-forest hover:bg-pe-gold/10 transition-colors"
          >
            Update password
          </button>
        </div>
      </div>
    </div>
  );
}

function GuestClaim({ flash, reload }) {
  const [available, setAvailable] = useState(false);
  const [stage, setStage] = useState(""); // "" | sent
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    api("/me/guest-history").then((d) => setAvailable(d.available)).catch(() => {});
  }, []);

  if (!available) return null;
  return (
    <div className="md:col-span-3 rounded-sm border border-pe-gold/50 bg-white p-5 text-sm text-pe-forest" data-testid="pe-guest-claim">
      <p className="mb-1">We found guest bookings made with your email. Attach them to this account so everything lives in one place.</p>
      {stage !== "sent" ? (
        <button
          data-testid="pe-guest-claim-start"
          onClick={async () => {
            setErr("");
            try { await api("/me/claim-guest", { method: "POST", body: {} }); setStage("sent"); }
            catch (e) { setErr(e.message); }
          }}
          className="mt-2 rounded-full border border-pe-brass/60 px-5 py-2 text-[10px] tracking-[0.2em] uppercase text-pe-forest hover:bg-pe-gold/10"
        >
          Email me a verification code
        </button>
      ) : (
        <div className="flex flex-wrap items-center gap-3 mt-2">
          <input data-testid="pe-guest-claim-code" inputMode="numeric" maxLength={6} placeholder="6-digit code" className="rounded-sm border border-pe-sand/70 px-3 py-2 text-sm tracking-[0.4em] w-40" value={code} onChange={(e) => setCode(e.target.value)} />
          <button
            data-testid="pe-guest-claim-verify"
            onClick={async () => {
              setErr("");
              try {
                const r = await api("/me/claim-guest/verify", { method: "POST", body: { code } });
                flash(`Done — ${r.merged} record(s) attached to your account.`);
                setAvailable(false);
                await reload();
              } catch (e) { setErr(e.message); }
            }}
            className="rounded-full bg-pe-forest text-pe-ivory px-5 py-2 text-[10px] tracking-[0.2em] uppercase"
          >
            Attach my bookings
          </button>
        </div>
      )}
      {err && <p className="text-red-800 text-[12px] mt-2">{err}</p>}
    </div>
  );
}

function ClientAppointment({ a, policies, reload }) {
  const [mode, setMode] = useState(""); // "" | cancel | reschedule
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [days, setDays] = useState({});
  const [newSlot, setNewSlot] = useState("");
  const [fee, setFee] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const manageable = ["confirmed", "pending_review"].includes(a.status) && new Date(a.start) > new Date();
  const payable = a.status === "pending_payment" && new Date(a.hold_until || 0) > new Date();

  useEffect(() => {
    if (mode === "reschedule") {
      api(`/availability?service_id=${a.service.id}&month=${month}`).then((d) => setDays(d.days)).catch(() => setDays({}));
    }
  }, [mode, month, a.service.id]);

  const cancel = async (confirmFee) => {
    setBusy(true);
    setErr("");
    try {
      const r = await api(`/bookings/${a.booking_id}/cancel`, { method: "POST", body: { confirm_fee: confirmFee } });
      if (r.requires_fee) {
        if (r.checkout_url) { window.location.href = r.checkout_url; return; }
        setFee(r.fee);
        return;
      }
      setMode("");
      await reload("Your appointment has been cancelled — a confirmation is on its way to your email.");
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const reschedule = async (confirmFee) => {
    if (!newSlot) return setErr("Please choose a new date and time.");
    setBusy(true);
    setErr("");
    try {
      const r = await api(`/bookings/${a.booking_id}/reschedule`, { method: "POST", body: { new_start: newSlot, confirm_fee: confirmFee } });
      if (r.requires_fee) {
        if (r.checkout_url) { window.location.href = r.checkout_url; return; }
        setFee(r.fee);
        return;
      }
      setMode("");
      await reload(`Rescheduled to ${fmtWhen(r.booking.start)} — your $25 deposit moved with it.${r.note ? " " + r.note : ""}`);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const hoursUntil = (new Date(a.start) - new Date()) / 36e5;
  const late = hoursUntil < (policies?.cancellation_window_hours || 24);

  return (
    <div data-testid={`pe-appointment-${a.booking_id}`} className="rounded-sm border border-pe-sand/60 bg-white p-5 mb-3">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-pe text-xl text-pe-forest">{a.service.name}</p>
          <p className="text-[13px] text-pe-muted">{fmtWhen(a.start)} · {a.service.duration_label}</p>
          <p className="text-[12px] text-pe-muted mt-1">
            {a.quote?.service_price ? `Service ${a.service.starting ? "from " : ""}${fmtMoney(a.quote.service_price)}` : "Price at consultation"}
            {(a.quote?.addons || []).filter((x) => x.amount > 0).map((x) => ` · ${x.label} +$${x.amount}`)}
            {a.quote?.final_price ? ` · Final price ${fmtMoney(a.quote.final_price)} (confirmed by the house)` : ""}
            {" · "}Deposit {a.payment_status === "paid" ? `paid ✓${a.deposit_transferred ? " (transferred)" : ""}` : "$25 due"}
            {a.quote?.balance !== null && a.quote?.balance !== undefined && ` · Balance ${fmtMoney(a.quote.balance)}`}
          </p>
          {a.status === "pending_payment" && <p className="text-[12px] text-amber-800 mt-1">Awaiting your deposit — this time is held only while checkout is open.</p>}
        </div>
        <span className={`rounded-full border px-3 py-1 text-[10px] tracking-[0.15em] uppercase ${STATUS_CLS[a.status] || ""}`}>{a.status.replace(/_/g, " ")}</span>
      </div>

      {payable && (
        <div className="mt-3">
          <button
            data-testid={`pe-pay-${a.booking_id}`}
            onClick={async () => {
              try { const { checkout_url } = await api(`/bookings/${a.booking_id}/pay`, { method: "POST", body: {} }); window.location.href = checkout_url; }
              catch (e) { setErr(e.message); }
            }}
            className="rounded-full bg-pe-brass text-pe-ivory px-5 py-2 text-[10px] tracking-[0.2em] uppercase hover:bg-pe-forest transition-colors"
          >
            Complete $25 deposit
          </button>
        </div>
      )}

      {manageable && (
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-pe-sand/50">
          <button data-testid={`pe-reschedule-${a.booking_id}`} onClick={() => { setMode(mode === "reschedule" ? "" : "reschedule"); setFee(null); setErr(""); }} className="rounded-full border border-pe-brass/60 px-4 py-1.5 text-[10px] tracking-[0.2em] uppercase text-pe-forest hover:bg-pe-gold/10">Reschedule</button>
          <button data-testid={`pe-cancel-${a.booking_id}`} onClick={() => { setMode(mode === "cancel" ? "" : "cancel"); setFee(null); setErr(""); }} className="rounded-full border border-red-300 px-4 py-1.5 text-[10px] tracking-[0.2em] uppercase text-red-800 hover:bg-red-50">Cancel</button>
        </div>
      )}
      {err && <p className="text-sm text-red-800 mt-3">{err}</p>}

      {mode === "cancel" && (
        <div className="mt-4 rounded-sm border border-red-200 bg-red-50/60 p-5 space-y-3" data-testid={`pe-cancel-panel-${a.booking_id}`}>
          <p className="text-[13px] text-pe-body leading-relaxed">{policies?.cancellation}</p>
          <p className="text-[13px] text-pe-forest">
            {late ? `You're within ${policies?.cancellation_window_hours || 24} hours of your appointment — a $${policies?.cancellation_late_fee ?? 20} late-cancellation fee applies, payable now. The fee is charged once.` : `You're more than ${policies?.cancellation_window_hours || 24} hours ahead — no cancellation fee applies.`}
          </p>
          <div className="flex gap-3 items-center">
            <button data-testid={`pe-cancel-confirm-${a.booking_id}`} disabled={busy} onClick={() => cancel(late)} className="rounded-full bg-red-800 text-white px-5 py-2 text-[10px] tracking-[0.2em] uppercase disabled:opacity-50">
              {busy ? "Working…" : late ? `Pay $${policies?.cancellation_late_fee ?? 20} fee & cancel` : "Cancel my appointment"}
            </button>
            <button onClick={() => setMode("")} className="text-[10px] tracking-[0.2em] uppercase text-pe-muted">Keep my appointment</button>
          </div>
        </div>
      )}

      {mode === "reschedule" && (
        <div className="mt-4 rounded-sm border border-pe-gold/40 bg-pe-cream/50 p-5 space-y-3" data-testid={`pe-reschedule-panel-${a.booking_id}`}>
          <p className="text-[13px] text-pe-body leading-relaxed">{policies?.rescheduling}</p>
          {late && <p className="text-[12px] text-pe-muted italic">You're rescheduling within 24 hours — the house's late-rescheduling rule is being finalized and will be shown here before any fee applies. No fee applies today.</p>}
          <input type="month" className={inputCls} value={month} onChange={(e) => { setMonth(e.target.value); setNewSlot(""); }} />
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {Object.keys(days).sort().map((d) => (
              <div key={d}>
                <p className="text-[10px] tracking-[0.2em] uppercase text-pe-olive">{new Date(d + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {days[d].map((iso) => (
                    <button key={iso} data-testid={`pe-reschedule-slot-${iso.slice(11, 16)}`} onClick={() => setNewSlot(iso)} className={`px-3 py-1.5 rounded-full text-[11px] border transition-colors ${newSlot === iso ? "bg-pe-forest text-pe-ivory border-pe-forest" : "border-pe-sand text-pe-body hover:border-pe-gold"}`}>
                      {new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {!Object.keys(days).length && <p className="text-[12px] text-pe-muted">No openings this month — try the next one.</p>}
          </div>
          <button data-testid={`pe-reschedule-confirm-${a.booking_id}`} disabled={busy || !newSlot} onClick={() => reschedule(false)} className="rounded-full bg-pe-forest text-pe-ivory px-5 py-2 text-[10px] tracking-[0.2em] uppercase disabled:opacity-50">
            {busy ? "Confirming…" : "Confirm new time"}
          </button>
        </div>
      )}
    </div>
  );
}

function FormsTab({ flash }) {
  const [data, setData] = useState(null);
  const [existing, setExisting] = useState({});
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    const d = await api("/me/forms");
    setData(d);
    for (const key of ["consultation", "intake"]) {
      const r = await api(`/me/forms/${key}`).catch(() => null);
      if (r?.form?.answers) setExisting((e) => ({ ...e, [key]: r.form.answers }));
    }
  };
  useEffect(() => { load().catch((e) => setErr(e.message)); }, []);

  if (!data) return <p className="font-pe italic text-lg text-pe-olive">{err || "Loading your forms…"}</p>;

  return (
    <div className="space-y-8">
      <div className="grid md:grid-cols-2 gap-4" data-testid="pe-forms-status">
        {["consultation", "intake"].map((key) => {
          const s = data.status[key];
          return (
            <div key={key} className={`rounded-sm border p-5 ${s.complete ? "border-pe-sage/60 bg-pe-cream/50" : "border-amber-300 bg-amber-50/60"}`} data-testid={`pe-form-status-${key}`}>
              <p className="font-pe text-lg text-pe-forest">{key === "consultation" ? "Consultation Form" : "Client Intake Form"}</p>
              <p className="text-[12px] mt-1 text-pe-body">
                {s.complete ? `Complete ✓ · updated ${s.updated_at ? new Date(s.updated_at).toLocaleDateString() : ""}` : `Required before booking — ${s.missing.length ? `${s.missing.length}+ answers missing` : "not started"}`}
              </p>
              {s.flags > 0 && <p className="text-[11px] text-pe-muted mt-1">{s.flags} answer{s.flags > 1 ? "s" : ""} flagged for your provider to review — a flag is never a diagnosis.</p>}
            </div>
          );
        })}
      </div>
      <p className="text-[12px] text-pe-muted leading-relaxed">
        Both forms are required parts of your profile and must be complete before any new booking. Safety answers are private — visible only to you and
        authorized staff — and every update is saved as a new dated version, so your history is preserved. The question set is a draft prepared for the
        house's final review.
      </p>
      {data.definitions.map((def) => (
        <div key={def.key} className="rounded-sm border border-pe-sand/60 bg-white p-7" data-testid={`pe-form-${def.key}`}>
          <p className="font-pe text-xl text-pe-forest mb-4">{def.title}</p>
          <PEFormRenderer
            definition={def}
            initial={existing[def.key] || {}}
            busy={busy === def.key}
            testPrefix={`pe-form-${def.key}`}
            onSubmit={async (answers) => {
              setBusy(def.key);
              setErr("");
              try {
                const r = await api(`/me/forms/${def.key}`, { method: "PUT", body: { answers } });
                setData((d) => ({ ...d, status: r.status }));
                flash(r.flags?.length ? `${def.title} saved — your provider will review a few answers with you before service (a flag is not a diagnosis).` : `${def.title} saved.`);
              } catch (e) { setErr(e.message); }
              finally { setBusy(""); }
            }}
          />
        </div>
      ))}
      {err && <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-sm px-4 py-3">{err}</p>}
    </div>
  );
}

function NotesTab({ notes, reload, flash }) {
  const [text, setText] = useState("");
  const [kind, setKind] = useState("note");
  const [err, setErr] = useState("");
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="rounded-sm border border-pe-sand/60 bg-white p-7 space-y-4">
        <p className="font-pe text-xl text-pe-forest">Leave a private note</p>
        <p className="text-[12px] text-pe-muted">Private notes are visible only to you and authorized staff. Nothing here is published.</p>
        <Field label="Type">
          <select className={inputCls} value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="note">Note for a future appointment</option>
            <option value="preference">Preference update</option>
            <option value="improvement">Something to improve</option>
            <option value="question">Question before my next visit</option>
            <option value="booking_feedback">Booking feedback</option>
            <option value="website_feedback">Website feedback</option>
            <option value="deletion_request">Account deletion request</option>
          </select>
        </Field>
        <Field label="Your note"><textarea data-testid="pe-note-text" rows={4} className={inputCls} value={text} onChange={(e) => setText(e.target.value)} /></Field>
        {err && <p className="text-sm text-red-800">{err}</p>}
        <button
          data-testid="pe-note-submit"
          onClick={async () => {
            setErr("");
            try { await api("/me/notes", { method: "POST", body: { kind, text } }); setText(""); await reload(); flash("Note saved privately."); }
            catch (e) { setErr(e.message); }
          }}
          className="rounded-full bg-pe-forest text-pe-ivory px-7 py-2.5 text-[10px] tracking-[0.25em] uppercase hover:bg-pe-olive transition-colors"
        >
          Save note
        </button>
      </div>
      <div className="space-y-3">
        {notes.map((n) => (
          <div key={n.note_id} className="rounded-sm border border-pe-sand/60 bg-white p-5">
            <div className="flex justify-between gap-3 mb-2">
              <span className="text-[10px] tracking-[0.2em] uppercase text-pe-olive">{n.kind.replace(/_/g, " ")}</span>
              <span className="text-[10px] text-pe-muted">{new Date(n.created_at).toLocaleDateString()} · {n.status}</span>
            </div>
            <p className="text-sm text-pe-body whitespace-pre-wrap">{n.text}</p>
            {n.staff_response && <p className="text-[12px] text-pe-olive mt-2 border-t border-pe-sand/50 pt-2">The house: {n.staff_response}</p>}
          </div>
        ))}
        {!notes.length && <p className="text-sm text-pe-muted">No notes yet.</p>}
      </div>
    </div>
  );
}

function ReviewsTab({ reviews, reviewable, reload, flash }) {
  const [form, setForm] = useState({ appointment_id: "", rating: 5, text: "", consent_public: false, consent_marketing: false });
  const [err, setErr] = useState("");
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="rounded-sm border border-pe-sand/60 bg-white p-7 space-y-4">
        <p className="font-pe text-xl text-pe-forest">Leave a review</p>
        <p className="text-[12px] text-pe-muted">Reviews open after a completed appointment. Nothing is ever published without your consent — and every public review is moderated first.</p>
        {reviewable.length ? (
          <>
            <Field label="Appointment">
              <select data-testid="pe-review-appointment" className={inputCls} value={form.appointment_id} onChange={(e) => setForm((f) => ({ ...f, appointment_id: e.target.value }))}>
                <option value="">Choose…</option>
                {reviewable.map((a) => <option key={a.booking_id} value={a.booking_id}>{a.service.name} — {new Date(a.start).toLocaleDateString()}</option>)}
              </select>
            </Field>
            <Field label="Rating">
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((r) => (
                  <button key={r} type="button" data-testid={`pe-review-star-${r}`} onClick={() => setForm((f) => ({ ...f, rating: r }))}
                    className={`w-9 h-9 rounded-full border text-sm transition-colors ${form.rating >= r ? "bg-pe-gold/20 border-pe-gold text-pe-brass" : "border-pe-sand text-pe-muted"}`}>★</button>
                ))}
              </div>
            </Field>
            <Field label="Your review"><textarea data-testid="pe-review-text" rows={4} className={inputCls} value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} /></Field>
            <label className="flex items-start gap-3 text-[13px] text-pe-body"><input type="checkbox" className="mt-1 accent-[#C59B27]" checked={form.consent_public} onChange={(e) => setForm((f) => ({ ...f, consent_public: e.target.checked }))} /><span>The house may publish this review (after moderation).</span></label>
            <label className="flex items-start gap-3 text-[13px] text-pe-body"><input type="checkbox" className="mt-1 accent-[#C59B27]" checked={form.consent_marketing} onChange={(e) => setForm((f) => ({ ...f, consent_marketing: e.target.checked }))} /><span>The house may use this review in marketing.</span></label>
            {err && <p className="text-sm text-red-800">{err}</p>}
            <button
              data-testid="pe-review-submit"
              onClick={async () => {
                setErr("");
                try { await api("/me/reviews", { method: "POST", body: form }); setForm({ appointment_id: "", rating: 5, text: "", consent_public: false, consent_marketing: false }); await reload(); flash("Review submitted for moderation — thank you."); }
                catch (e) { setErr(e.message); }
              }}
              className="rounded-full bg-pe-forest text-pe-ivory px-7 py-2.5 text-[10px] tracking-[0.25em] uppercase hover:bg-pe-olive transition-colors"
            >
              Submit review
            </button>
          </>
        ) : (
          <p className="text-sm text-pe-muted">No completed appointments waiting on a review.</p>
        )}
      </div>
      <div className="space-y-3">
        {reviews.map((r) => (
          <div key={r.review_id} className="rounded-sm border border-pe-sand/60 bg-white p-5">
            <div className="flex justify-between mb-2">
              <span className="font-pe text-lg text-pe-forest">{r.service}</span>
              <span className="text-pe-brass">{"★".repeat(r.rating)}</span>
            </div>
            <p className="text-sm text-pe-body">{r.text}</p>
            <p className="text-[10px] tracking-[0.2em] uppercase text-pe-muted mt-2">{r.status}{r.consent_public ? " · may publish" : " · private"}</p>
          </div>
        ))}
        {!reviews.length && <p className="text-sm text-pe-muted">No reviews yet.</p>}
      </div>
    </div>
  );
}
